import { fmt, monthYearOf, niceCeil, withUnit } from './format';

// the first year with a reading has no km driven: "new" for a delivery reading, else the reading itself
const firstReadingLabel = (km, t) => {
    if (km == null) return '—';
    return km <= 100 ? t('years.new') : fmt(km);
};

const droveLabel = (d, t) => (d < 0 ? t('years.dropped', { km: fmt(-d) }) : t('years.drove', { km: fmt(d) }));

// Km driven per year (between the last readings of consecutive years), with one dot per visit or repair
export const buildYears = (events, { selectedYear, canFilter }, t) => {
    const dated = events.map(e => ({ ...e, ...monthYearOf(e) })).filter(e => e.y);
    if (!dated.length) return [];

    const years = dated.map(e => e.y);
    const [first, last] = [Math.min(...years), Math.max(...years)];
    const lastKm = {};
    dated
        .slice()
        .sort((a, b) => a.y * 12 + a.m - (b.y * 12 + b.m))
        .forEach(e => {
            if (e.km != null) lastKm[e.y] = e.km;
        });

    const drove = {};
    let previousKm = null;
    for (let y = first; y <= last; y++) {
        if (lastKm[y] != null) {
            drove[y] = previousKm == null ? null : lastKm[y] - previousKm;
            previousKm = lastKm[y];
        }
    }
    const maxDrove = Math.max(1, ...Object.values(drove).map(d => d || 0));

    const result = [];
    for (let y = first; y <= last; y++) {
        const inYear = dated.filter(e => e.y === y);
        const d = drove[y];
        let bar;
        let kmLabel;
        if (!inYear.length) [bar, kmLabel] = [{ kind: 'none' }, t('years.noVisits')];
        else if (d == null) [bar, kmLabel] = [{ kind: 'first' }, firstReadingLabel(lastKm[y], t)];
        else if (d < 0) [bar, kmLabel] = [{ kind: 'drop' }, `−${fmt(-d)}`];
        else
            [bar, kmLabel] = [
                {
                    kind: 'drove',
                    height: Math.max(6, Math.round((d / maxDrove) * 100)),
                    flag: inYear.some(e => e.flag),
                },
                `+${fmt(d)}`,
            ];

        const visits = inYear.length ? t('years.visits', { count: inYear.length }) : t('years.noVisits');
        const km = d != null ? `, ${droveLabel(d, t)}` : '';

        result.push({
            year: y,
            bar,
            kmLabel,
            warn: d != null && d < 0,
            dim: !inYear.length,
            dots: inYear.map(e => !!e.flag),
            on: selectedYear === y,
            disabled: !inYear.length || !canFilter,
            aria: `${y}: ${visits}${km}`,
        });
    }

    return result;
};

const dotKind = (isDrop, last, inspection) => {
    if (isDrop) return 'warn';
    if (inspection) return 'insp';
    return last ? 'last' : '';
};

// a reading label closer than this (% of the width) to the one before it is left out, so labels do not overlap
const LABEL_GAP = 11;

const timeOf = p => p.y + (p.m - 1) / 12 + ((p.d || 15) - 1) / 365;

// Mileage chart on a 0-100 % plot: solid line, dashed 12+ month gaps, amber drops, labels for the key readings.
// `pointLabels` (two-column layout): a filled area under the line and a label on every reading that has room
export const buildMileageChart = (points, t, { pointLabels = false } = {}) => {
    if (!points || points.length < 2) return null;

    const times = points.map(timeOf);
    const xMin = Math.floor(Math.min(...times));
    const xMax = Math.max(Math.ceil(Math.max(...times) + 0.001), xMin + 1);
    const yMax = niceCeil(Math.max(...points.map(p => p.km)) * 1.08);
    const X = time => ((time - xMin) / (xMax - xMin)) * 100;
    const Y = km => 100 - (km / yMax) * 100;
    const step = xMax - xMin > 10 ? 2 : 1;
    const few = points.length <= 4;

    const chart = {
        count: points.length,
        grid: [0, 1, 2, 3, 4].map(k => ({
            top: 100 - k * 25,
            label: k === 0 ? withUnit(0, 'km') : fmt((yMax * k) / 4),
        })),
        xticks: [],
        solid: '',
        gap: '',
        drop: '',
        dots: [],
        labels: [],
        gapLabels: [],
        drops: [],
        area: '',
        inspection: points.some(p => p.inspection),
    };
    for (let year = xMin; year <= xMax; year += step) chart.xticks.push({ left: X(year), label: String(year) });

    points.forEach((p, i) => {
        const [x, y] = [X(times[i]), Y(p.km)];
        const previous = i > 0 ? points[i - 1] : null;
        const isDrop = !!previous && p.km < previous.km;
        const last = i === points.length - 1;

        if (previous) {
            const segment = ` M${(X(times[i - 1]) * 10).toFixed(1)} ${(Y(previous.km) * 10).toFixed(1)} L${(x * 10).toFixed(1)} ${(y * 10).toFixed(1)}`;
            if (isDrop) {
                chart.drop += segment;
                chart.drops.push(
                    t('mileage.dropLine', {
                        date: p.date,
                        km: fmt(p.km),
                        diff: fmt(previous.km - p.km),
                        prevDate: previous.date,
                        prevKm: fmt(previous.km),
                    })
                );
            } else if (times[i] - times[i - 1] >= 1) {
                chart.gap += segment;
                const months = Math.round((times[i] - times[i - 1]) * 12);
                chart.gapLabels.push({
                    left: (X(times[i - 1]) + x) / 2,
                    top: (Y(previous.km) + y) / 2,
                    text: t('mileage.gapLabel', { count: months }),
                });
            } else {
                chart.solid += segment;
            }
        }

        chart.dots.push({
            left: x,
            top: y,
            kind: dotKind(isDrop, last, p.inspection),
            title: `${p.date} · ${withUnit(fmt(p.km), 'km')}`,
        });
        if (isDrop)
            chart.labels.push({
                left: x,
                top: y,
                warn: true,
                text: `−${withUnit(fmt(previous.km - p.km), 'km')}`,
                sub: `${p.date} · ${withUnit(fmt(p.km), 'km')}`,
            });
        else if (pointLabels) {
            const previousLabel = chart.labels[chart.labels.length - 1];
            const crowded = previousLabel && x - previousLabel.left < LABEL_GAP;
            // the latest reading always gets its label; it takes the place of a crowded one before it
            if (crowded && last && !previousLabel.warn) chart.labels.pop();
            if (!crowded || last)
                chart.labels.push({
                    left: x,
                    top: y,
                    text: withUnit(fmt(p.km), 'km'),
                    sub: last ? p.date : '',
                    insp: !!p.inspection,
                });
        } else if (few)
            chart.labels.push({
                left: x,
                top: y,
                text: p.km === 0 ? t('mileage.registration') : withUnit(fmt(p.km), 'km'),
                sub: p.date,
            });
        else if (i === 0 && p.km <= 10)
            chart.labels.push({
                left: x,
                top: y,
                above: true,
                text: t('mileage.registrationAt', { km: fmt(p.km) }),
                sub: '',
            });
        else if (last) chart.labels.push({ left: x, top: y, text: withUnit(fmt(p.km), 'km'), sub: p.date });
    });

    if (pointLabels) {
        const coords = points.map((p, i) => [(X(times[i]) * 10).toFixed(1), (Y(p.km) * 10).toFixed(1)]);
        const line = coords.map(([x, y]) => `L${x} ${y}`).join(' ');
        chart.area = `M${coords[0][0]} 1000 ${line} L${coords[coords.length - 1][0]} 1000 Z`;
    }

    const [first, final] = [points[0], points[points.length - 1]];
    chart.dropTitle = t('mileage.dropTitle', { count: chart.drops.length });
    chart.aria =
        t('mileage.aria', { from: first.date, fromKm: fmt(first.km), to: final.date, toKm: fmt(final.km) }) +
        (chart.drops.length ? t('mileage.ariaDrops', { count: chart.drops.length }) : '');

    return chart;
};
